import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import {
	EidEasySignStatus,
	OtpContextBase,
	OtpResendOutcome,
} from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { maskContact } from '@dealstack/shared/lib/common/maskContact';
import { SignerContactType } from '@dealstack/shared/lib/generated';
import type { EidEasyCompleteRes } from '@dealstack/shared/lib/generated';
import {
	classifyOtpSubmitFailure,
	dispatchOtpCode,
	makeEidEasyClient,
	parseOtpRateLimitSeconds,
	toErrorKey,
	validateOtpCode,
} from './useEidEasySign.utils';
import type { SigningTransport } from './useEidEasySignCore.types';

/**
 * Shared primitives the OTP flow borrows from the signing core: the transport,
 * the error policy, the unmount guard, and the status/error setters that drive
 * the modal's shared state machine.
 */
export interface OtpFlowDeps {
	transport: SigningTransport;
	captureError: (err: unknown, phase: string) => void;
	onComplete?: (result?: EidEasyCompleteRes) => void;
	unmountedRef: MutableRefObject<boolean>;
	setStatus: (status: EidEasySignStatus) => void;
	setError: (error: string | null) => void;
	runFlow: (phase: string, fn: () => Promise<void>) => Promise<void>;
}

/**
 * The eID Easy OTP sub-flow: prepare-and-dispatch, inline-retry submit, and
 * resend with server-cooldown handling. Extracted from `useEidEasySignCore` so
 * the (sizeable) OTP state and logic live in one place. It owns the OTP context
 * and the verify/attempts/code-error state, and reports progress through the
 * core's shared `setStatus`/`setError`.
 *
 * Two-call SDK shape: `dispatchOtpCode` (token-less `start` → sends the SMS/
 * email) on entry, then `validateOtpCode` (token `start` → re-validates against
 * the same docId, no new dispatch) per submit, so wrong codes can be retried
 * without sending a fresh code.
 */
export const useEidEasyOtpFlow = ({
	transport,
	captureError,
	onComplete,
	unmountedRef,
	setStatus,
	setError,
	runFlow,
}: OtpFlowDeps) => {
	const [otpContext, setOtpContext] = useState<OtpContextBase | null>(null);
	const [otpVerifying, setOtpVerifying] = useState(false);
	const [otpCodeError, setOtpCodeError] = useState<string | null>(null);
	const [otpAttempts, setOtpAttempts] = useState(0);
	const otpResendingRef = useRef(false);

	/** Prepare + dispatch the OTP, then land on the code-entry step. */
	const runOtpFlow = useCallback(
		async (channel: SignerContactType, value: string, signatureBase64: string) => {
			setStatus(EidEasySignStatus.PREPARING);
			setError(null);

			await runFlow('otp-prepare', async () => {
				const prep = await transport.prepare(EidEasySigningMethod.OTP, {
					otpContact: { channel, value },
					drawnSignatureBase64: signatureBase64,
				});

				setOtpContext({
					channel,
					maskedContact: maskContact(value, channel),
					docId: prep.docId,
					clientId: prep.clientId,
					baseUrl: prep.baseUrl,
					mockMode: prep.mockMode ?? false,
				});

				if (!prep.mockMode) {
					await dispatchOtpCode(makeEidEasyClient(prep));
				}

				if (unmountedRef.current) {
					return;
				}
				setOtpAttempts(0);
				setOtpCodeError(null);
				setStatus(EidEasySignStatus.OTP_CODE_ENTRY);
			});
		},
		[transport, runFlow, setStatus, setError, unmountedRef]
	);

	const submitOtpCode = useCallback(
		async (code: string) => {
			if (!otpContext || otpVerifying || otpResendingRef.current) {
				return;
			}
			setOtpVerifying(true);
			setOtpCodeError(null);

			try {
				// Phase 1: validate the OTP code via the SDK (skipped in mockMode).
				// Only validation failures feed classifyOtpSubmitFailure.
				if (!otpContext.mockMode) {
					try {
						await validateOtpCode(
							makeEidEasyClient(otpContext),
							otpContext.channel,
							code
						);
					} catch (validationErr: unknown) {
						captureError(validationErr, 'otp-submit');
						if (unmountedRef.current) {
							return;
						}
						const { fatal, errorKey } = classifyOtpSubmitFailure(
							validationErr,
							otpAttempts
						);
						if (fatal) {
							setError(errorKey);
							setStatus(EidEasySignStatus.ERROR);
						} else {
							setOtpAttempts((n) => n + 1);
							setOtpCodeError(errorKey);
						}
						return;
					}
				}

				// Phase 2: validation passed (or mockMode). Complete the signing.
				// Any failure here is always fatal — the code was correct.
				if (unmountedRef.current) {
					return;
				}
				setStatus(EidEasySignStatus.COMPLETING);
				try {
					const result = await transport.complete(otpContext.docId);
					if (unmountedRef.current) {
						return;
					}
					setStatus(EidEasySignStatus.DONE);
					setOtpContext(null);
					onComplete?.(result);
				} catch (completionErr: unknown) {
					captureError(completionErr, 'otp-submit');
					if (unmountedRef.current) {
						return;
					}
					setError(toErrorKey(completionErr));
					setStatus(EidEasySignStatus.ERROR);
				}
			} finally {
				if (!unmountedRef.current) {
					setOtpVerifying(false);
				}
			}
		},
		[
			otpContext,
			otpVerifying,
			otpAttempts,
			transport,
			onComplete,
			captureError,
			setStatus,
			setError,
			unmountedRef,
		]
	);

	const resendOtpCode = useCallback(async (): Promise<OtpResendOutcome> => {
		if (!otpContext || otpResendingRef.current || otpVerifying) {
			return { status: 'failed' };
		}
		otpResendingRef.current = true;
		setOtpCodeError(null);
		try {
			if (!otpContext.mockMode) {
				await dispatchOtpCode(makeEidEasyClient(otpContext));
			}
			if (unmountedRef.current) {
				return { status: 'sent' };
			}
			setOtpAttempts(0);
			return { status: 'sent' };
		} catch (err: unknown) {
			if (unmountedRef.current) {
				return { status: 'failed' };
			}
			// eID Easy enforces its own resend cooldown. A "wait N seconds" rejection
			// is not fatal — keep the user on the entry screen so the UI can re-arm
			// its countdown to the server's remaining window.
			const waitSeconds = parseOtpRateLimitSeconds(err);
			if (waitSeconds !== null) {
				return { status: 'rate_limited', waitSeconds };
			}
			captureError(err, 'otp-resend');
			setError(toErrorKey(err));
			setStatus(EidEasySignStatus.ERROR);
			return { status: 'failed' };
		} finally {
			otpResendingRef.current = false;
		}
	}, [otpContext, otpVerifying, captureError, setStatus, setError, unmountedRef]);

	const resetOtpContext = useCallback(() => {
		setOtpContext(null);
		setOtpVerifying(false);
		setOtpCodeError(null);
		setOtpAttempts(0);
	}, []);

	return {
		runOtpFlow,
		submitOtpCode,
		resendOtpCode,
		resetOtpContext,
		otpVerifying,
		otpCodeError,
		otpChannel: otpContext?.channel,
		otpMaskedContact: otpContext?.maskedContact,
	};
};
